import React from 'react'
import { FaUser, FaCalendarAlt, FaLaptop, FaDesktop, FaMobile, FaTabletAlt, FaPrint } from 'react-icons/fa'
import dayjs from 'dayjs'
import 'dayjs/locale/id'
import Edit from '../action/Edit'
import Hapus from '../action/Hapus'
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
// Import PrimeReact CSS
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import { LaptopMinimal, MonitorCheck, Smartphone, Tablet, Printer } from 'lucide-react'

// Set locale Indonesia untuk dayjs
dayjs.locale('id');

function DataAudit({ rows, getDeviceIcon, handleDelete, handleDetail, handleEdit, loading = false }) {

  // Template untuk nama pegawai dan NIP
  const namaPegawaiTemplate = (rowData) => {
    return (
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <FaUser className="text-blue-600 w-4 h-4" />
        </div>
        <div>
          <p className="font-semibold text-gray-900">{rowData.gupeg || '-'}</p>
          <p className="text-sm text-gray-500">NIP: {rowData.nip || '-'}</p>
        </div>
      </div>
    );
  };
  // Template untuk jenis perangkat
  const jenisPerangkatTemplate = (rowData) => {
    const iconMap = {
      'laptop': <LaptopMinimal className="text-gray-400 text-sm" />,
      'desktop': <MonitorCheck className="text-gray-400 text-sm" />,
      'tablet': <Tablet className="text-gray-400 text-sm" />,
      'mobile': <Smartphone className="text-gray-400 text-sm" />,
      'printer': <Printer className="text-gray-400 text-sm" />
    };
    const icon = iconMap[rowData.jenis_perangkat?.toLowerCase()] || '💻';

    return (
      <div className="flex items-center gap-2">
        <span className="text-lg">{icon}</span>
        <span className="text-sm font-medium text-gray-900">{rowData.jenis_perangkat || '-'}</span>
      </div>
    );
  };

  // Template untuk hasil audit
  const hasilAuditTemplate = (rowData) => {
    const statusMap = {
      'Aman': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
      'Perlu Update': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
      'Berisiko': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' }
    };
    const status = statusMap[rowData.hasil] || { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${status.bg} ${status.text} ${status.border}`}>
        {rowData.hasil || '-'}
      </span>
    );
  };

  // Template untuk aksi
  const aksiTemplate = (rowData) => {
    return (
      <div className="flex items-center justify-center gap-2">
        <Edit id={rowData.id} handleEdit={handleEdit} />
        <Hapus id={rowData.id} handleDelete={handleDelete} />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-gray-600">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <DataTable
        value={rows}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Menampilkan {first} sampai {last} dari {totalRecords} data"
        emptyMessage="Tidak ada data audit yang tersedia"
        loading={loading}
        stripedRows
        showGridlines
        responsiveLayout="scroll"
        className="p-datatable-sm"
      >
        <Column
          field="gupeg"
          header="Nama Pegawai / NIP"
          body={namaPegawaiTemplate}
          style={{ minWidth: '300px' }}
          sortable
        />
        <Column
          field="jenis_perangkat"
          header="Jenis Perangkat"
          body={jenisPerangkatTemplate}
          style={{ minWidth: '200px' }}
          sortable
        />
        <Column
          field="model"
          header="Model"
          style={{ minWidth: '150px' }}
          sortable
        />
        <Column
          field="so"
          header="Sistem Operasi"
          style={{ minWidth: '200px' }}
          sortable
        />
        <Column
          field="so_update"
          header="Update SO"
          body={(rowData) => {
            const soUpdate = rowData.so_update;
            if (!soUpdate) return '-';

            const isUpdated = soUpdate.includes('ya');
            return (
              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${isUpdated
                ? 'bg-green-100 text-green-800 border-green-200'
                : 'bg-red-100 text-red-800 border-red-200'
                }`}>
                {isUpdated ? 'Terupdate' : 'Belum Update'}
              </span>
            );
          }}
          style={{ minWidth: '200px' }}
          sortable
        />
        <Column
          field="tanggal_audit"
          header="Tanggal Audit"
          body={(rowData) => {
            if (!rowData.tgl) return '-';
            return (
              <div className="flex items-center gap-2">
                <FaCalendarAlt className="text-gray-400 text-sm" />
                <span className="text-sm text-gray-900">{dayjs(rowData.tgl).format('DD-MM-YYYY')}</span>
              </div>
            );
          }}
          style={{ minWidth: '150px' }}
          sortable
        />
        <Column
          field="lokasi"
          header="Lokasi"
          style={{ minWidth: '120px' }}
          sortable
        />
        <Column
          field="hasil"
          header="Hasil Audit"
          body={hasilAuditTemplate}
          style={{ minWidth: '300px' }}
          sortable
        />
        <Column
          field="id"
          header="Aksi"
          body={aksiTemplate}
          style={{ minWidth: '100px', textAlign: 'center' }}
          sortable={false}
        />
      </DataTable>
    </div>
  );
}

export default DataAudit